import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { format, isValid, parseISO } from 'date-fns';

import API from '@/apiRequest';
import { useAsyncService } from '@/contexts/AsyncServiceContext';
import {
  setInitialFilteredInspectors,
  setInitialFilteredPendingOrders,
  setInspectors,
  setInspectorsAvailabilities,
  setInspectorsUnavailabilities,
  setOrders,
  setPendingOrders,
  setUnsaved,
} from '@/store/ducks/schedule';
import { getScheduleDate } from '@/store/selectors/schedule';

const useScheduleData = () => {
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [inspectorsLoading, setInspectorsLoading] = useState(true);
  const [pendingOrdersLoading, setPendingOrdersLoading] = useState(true);
  const [pendingOrdersError, setPendingOrdersError] = useState(false);

  const date = useSelector(getScheduleDate);
  const dispatch = useDispatch();
  const { on, off } = useAsyncService();

  const fetchOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);

      const formattedSchedulesDate = format(date, 'yyyy-MM-dd');
      const parsedDate = parseISO(formattedSchedulesDate);

      if (!isValid(parsedDate)) {
        throw new Error('Invalid schedule date provided');
      }

      const { data } = await API.get('/scheduler/schedules', {
        params: {
          date: formattedSchedulesDate,
        },
      });

      dispatch(setOrders(data.orders));
      dispatch(setUnsaved(data.status === 'draft'));
    } catch (err) {
      console.debug('useScheduleData.fetchOrders error', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [date, dispatch]);

  const fetchInspectors = useCallback(async () => {
    try {
      setInspectorsLoading(true);

      const formattedSchedulesDate = format(date, 'yyyy-MM-dd');
      const requests = [
        API.get('/assignables'),
        API.get('/scheduler/inspector-unavailabilities', {
          params: { date: formattedSchedulesDate },
        }),
      ];

      const [{ data: assignables }, { data: unavailabilities }] = await Promise.all(requests);

      dispatch(setInitialFilteredInspectors(assignables));
      dispatch(setInspectors(assignables));
      dispatch(setInspectorsAvailabilities());
      dispatch(setInspectorsUnavailabilities(unavailabilities || []));
    } catch (err) {
      console.debug('useScheduleData.fetchInspectors error', err);
    } finally {
      setInspectorsLoading(false);
    }
  }, [date, dispatch]);

  const fetchPendingOrders = useCallback(async () => {
    try {
      setPendingOrdersLoading(true);
      setPendingOrdersError(false);

      const { data } = await API.get('/scheduler/assignable-orders');

      dispatch(setInitialFilteredPendingOrders(data));
      dispatch(setPendingOrders(data));
    } catch (err) {
      setPendingOrdersError(true);
      console.debug('useScheduleData.fetchPendingOrders error', err);
    } finally {
      setPendingOrdersLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchOrders();
    fetchInspectors();
  }, [fetchInspectors, fetchOrders]);

  useEffect(() => {
    fetchPendingOrders();
  }, [fetchPendingOrders]);

  useEffect(() => {
    const refreshScheduleData = () => {
      fetchOrders();
      fetchInspectors();
      fetchPendingOrders();
    };

    on('connect', refreshScheduleData);

    return () => off('connect', refreshScheduleData);
  }, [fetchInspectors, fetchOrders, fetchPendingOrders, off, on]);

  return {
    ordersLoading,
    inspectorsLoading,
    pendingOrdersLoading,
    pendingOrdersError,
  };
};

export default useScheduleData;
